
import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ImageIcon, X, Loader2 } from "lucide-react";

const CreatePostForm = ({ onPostCreated }: { onPostCreated?: () => void }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const loadProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  }, [user]);
  
  React.useEffect(() => {
    loadProfile();
  }, [loadProfile]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to create a post",
        variant: "destructive"
      });
      return;
    }
    
    if (!content.trim() && selectedFiles.length === 0) {
      toast({
        title: "Empty post",
        description: "Please add some text or an image to your post",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Create post
      const { data: postData, error: postError } = await supabase
        .from('network_posts')
        .insert({
          user_id: user.id,
          content: content.trim() || null
        })
        .select('id')
        .single();
      
      if (postError) throw postError;
      
      // Upload media if any
      if (selectedFiles.length > 0 && postData) {
        setUploading(true);
        
        await Promise.all(selectedFiles.map(async (file) => {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;
          
          // Upload to Supabase Storage
          const { data: storageData, error: storageError } = await supabase
            .storage
            .from('post-media')
            .upload(filePath, file);
            
          if (storageError) throw storageError;
          
          // Get public URL
          const { data: publicUrlData } = supabase
            .storage
            .from('post-media')
            .getPublicUrl(filePath);
            
          // Create media record
          await supabase
            .from('post_media')
            .insert({
              post_id: postData.id,
              media_url: publicUrlData.publicUrl,
              media_type: file.type
            });
        }));
        
        setUploading(false);
      }
      
      setContent("");
      setSelectedFiles([]);
      toast({ title: "Success", description: "Your post has been published" });
      
      if (onPostCreated) {
        onPostCreated();
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast({
        title: "Error",
        description: "Failed to create your post",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      // Limit to 4 files total
      const newFiles = [...selectedFiles, ...filesArray].slice(0, 4);
      setSelectedFiles(newFiles);
    }
  };
  
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };
  
  return (
    <Card className="mb-6 shadow-sm">
      <form onSubmit={handleSubmit}>
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback>{getInitials(profile?.full_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <Textarea
                placeholder="What's on your mind?"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="resize-none min-h-20 bg-muted/20 focus-within:bg-background"
              />
              
              {selectedFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="bg-muted/20 rounded p-2 border flex items-center">
                        <div className="truncate flex-1 text-sm">{file.name}</div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 bg-background rounded-full p-1 hover:bg-destructive/10"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between border-t pt-3">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || selectedFiles.length >= 4}
            >
              <ImageIcon className="h-4 w-4 mr-1" />
              {selectedFiles.length > 0 ? `${selectedFiles.length}/4` : 'Add Media'}
            </Button>
          </div>
          
          <Button
            type="submit"
            disabled={isSubmitting || uploading || (!content.trim() && selectedFiles.length === 0)}
          >
            {isSubmitting || uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                {uploading ? 'Uploading...' : 'Posting...'}
              </>
            ) : (
              'Post'
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default CreatePostForm;
